import { useEffect, useState, useCallback } from "react";
import { bridge, BridgeChannels, BridgeActions } from "./bridge";

type Theme = "system" | "light" | "dark";

const STORAGE_KEY = "theme-preference";

// Track native theme state globally
let nativeTheme: "light" | "dark" | null = null;
const nativeThemeListeners = new Set<(theme: "light" | "dark") => void>();

function notifyNativeThemeListeners(theme: "light" | "dark") {
  nativeTheme = theme;
  nativeThemeListeners.forEach(listener => listener(theme));
}

function getSystemTheme(): "light" | "dark" {
  // If we have a native theme, use it
  if (nativeTheme !== null) {
    return nativeTheme;
  }
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function getStoredTheme(): Theme {
  if (typeof window === "undefined") return "system";
  return (localStorage.getItem(STORAGE_KEY) as Theme) || "system";
}

/**
 * Mirrors the preference onto the native shell so the status bar and navigation
 * bar match the page. Without it a user on "dark" inside a light-mode OS got a
 * dark page framed by light system chrome.
 *
 * Passing "system" is meaningful: it tells the shell to stop overriding and
 * follow the OS again. Fire-and-forget — older builds have no handler for this,
 * and a shell that doesn't keep up is cosmetic, never a reason to block the
 * page from theming itself.
 */
function pushThemeToNative(theme: Theme) {
  if (!bridge.isNative) return;
  bridge
    .request(BridgeChannels.DEVICE, BridgeActions.SET_THEME, { theme })
    .catch(() => {
      /* no handler on this build, or the bridge isn't up yet */
    });
}

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  const effectiveTheme = theme === "system" ? getSystemTheme() : theme;

  if (effectiveTheme === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
}

// Initialize theme immediately to prevent flash
if (typeof window !== "undefined") {
  applyTheme(getStoredTheme());

  // Set up native theme listener early
  if (bridge.isNative) {
    // Listen for theme changes from native
    bridge.on(BridgeChannels.DEVICE, BridgeActions.THEME_CHANGED, (payload: { theme: string; isDark: boolean }) => {
      const newTheme = payload.isDark ? "dark" : "light";
      notifyNativeThemeListeners(newTheme);
      // Re-apply theme if using system preference
      const storedTheme = getStoredTheme();
      if (storedTheme === "system") {
        applyTheme("system");
      }
    });

    // Request initial theme from native
    bridge.request<{ theme: string; isDark: boolean }>(BridgeChannels.DEVICE, BridgeActions.GET_THEME)
      .then((payload) => {
        const newTheme = payload.isDark ? "dark" : "light";
        notifyNativeThemeListeners(newTheme);
        const storedTheme = getStoredTheme();
        if (storedTheme === "system") {
          applyTheme("system");
        }
        // Sent once the round trip above proves the bridge is up, so a cold
        // start reconciles the shell with what this device actually stored —
        // the shell persists its own copy, but only the web knows for certain.
        pushThemeToNative(storedTheme);
      })
      .catch(() => {
        // Fallback to media query if native request fails
      });
  }
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(getStoredTheme);
  const [, forceUpdate] = useState({});

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem(STORAGE_KEY, newTheme);
    applyTheme(newTheme);
    pushThemeToNative(newTheme);
    // Dispatch a custom event so other useTheme instances can sync
    window.dispatchEvent(new CustomEvent('theme-change', { detail: newTheme }));
  }, []);

  // Listen for theme changes from other components
  useEffect(() => {
    const handleThemeChange = (e: Event) => {
      const newTheme = (e as CustomEvent<Theme>).detail;
      setThemeState(newTheme);
    };
    window.addEventListener('theme-change', handleThemeChange);
    return () => window.removeEventListener('theme-change', handleThemeChange);
  }, []);

  // Listen for native theme changes
  useEffect(() => {
    if (bridge.isNative) {
      const handleNativeThemeChange = () => {
        if (theme === "system") {
          applyTheme("system");
          forceUpdate({});
        }
      };
      nativeThemeListeners.add(handleNativeThemeChange);
      return () => {
        nativeThemeListeners.delete(handleNativeThemeChange);
      };
    }
  }, [theme]);

  // Listen for system theme changes when using system preference (web fallback)
  useEffect(() => {
    if (bridge.isNative) return; // Skip for native, handled above

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = () => {
      if (theme === "system") {
        applyTheme("system");
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [theme]);

  // Apply theme on mount
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const effectiveTheme = theme === "system" ? getSystemTheme() : theme;

  return { theme, setTheme, effectiveTheme };
}

